import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import { CheckCircle, Star, Users } from 'lucide-react';

const CloudSecurityEngineerProjectsPage = () => {
  const [expandedModules, setExpandedModules] = useState({});

  const toggleModule = (index) => {
    setExpandedModules(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const courseModules = [
    {
      title: "Introduction",
      lessons: [
        "Welcome",
        "Getting Technical Support for these 5 Projects [Slack Community]",
        "Course Introduction",
        "Project Costs Breakdown"
      ]
    },
    {
      title: "1. Secure Web Hosting on AWS: S3, CloudFront & WAF",
      lessons: [
        "Project Overview",
        "Create and Configure S3 Bucket for the Website",
        "Speed & Security with Amazon CloudFront",
        "Securing Website with AWS WAF",
        "Secure Access with AWS IAM",
        "Monitoring & Compliance",
        "Conclusion and Clean-up"
      ]
    },
    {
      title: "2. Identity & Access Security with Cognito and API Gateway",
      lessons: [
        "Project Overview",
        "Setting up Cognito User Pool",
        "Configure App Client & Domain",
        "Create Users and Assign Groups",
        "Frontend Integration with Cognito (OIDC Quick Setup)",
        "Conclusion and Clean-up"
      ]
    },
    {
      title: "3. Cloud Threat Detection & Security Audit",
      lessons: [
        "Project Overview",
        "Deploy Intentionally Insecure Resources",
        "Enable GuardDuty for Threat Detection",
        "Enable AWS Config Rules for Compliance Monitoring",
        "Lambda Audit Function (Custom Security Audit)",
        "Alerts via SNS",
        "Conclusion and Clean-Up"
      ]
    },
    {
      title: "4. Secure File Sharing & Malware Protection",
      lessons: [
        "Project Overview",
        "S3 & IAM Setup",
        "S3 Object Lambda Setup",
        "Amazon Macie Setup",
        "CloudFront and AWS Shield",
        "Conclusion and Clean-up"
      ]
    },
    {
      title: "5. Secrets Management & Encryption for Applications",
      lessons: [
        "Project Overview",
        "Database Setup",
        "Store Secrets in Secrets Manager",
        "Lambda Setup & Prerequisites",
        "Fetch Secrets & Connect to RDS",
        "Conclusion & Cleanup"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PromoBanner />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Side - Course Info */}
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-cyan-600 mb-6">
                  5 AWS Projects to Become a Cloud Security Engineer
                </h1>
                
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Complete Step-By-Step Guide to five AWS Projects to help you get hired as a Cloud Security Engineer. Build these high-quality projects, and add them into your portfolio! These projects are designed to boost your Cloud experience and help you build Cloud Security skills.
                </p>
                
                <div className="flex items-center gap-2 mb-8">
                  <Users size={20} className="text-blue-500" />
                  <span className="text-lg font-medium text-gray-700">Join security professionals!</span>
                  <Star size={20} className="text-yellow-500 fill-current" />
                </div>
                
                <a 
                  href="https://learn.zerotocloud.co/p/5-aws-projects-to-become-a-cloud-security-engineer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200 inline-block text-center"
                >
                  Enroll Now - $69
                </a>
              </div>
              
              {/* Right Side - Course Thumbnail */}
              <div className="relative">
                <img 
                  src="/course-covers/cloud-security-engineer-projects.png"
                  alt="5 AWS Projects to Become a Cloud Security Engineer"
                  className="w-full rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Content */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Course Content
            </h2>
            
            <div className="space-y-4">
              {courseModules.map((module, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleModule(index)}
                    className="w-full text-left p-6 bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                  >
                    <span className="font-medium text-gray-900">{module.title}</span>
                    <span className="text-gray-500">
                      {expandedModules[index] ? '−' : '+'}
                    </span>
                  </button>
                  
                  {expandedModules[index] && (
                    <div className="p-6 bg-white">
                      <ul className="space-y-2">
                        {module.lessons.map((lesson, lessonIndex) => (
                          <li key={lessonIndex} className="flex items-center gap-3">
                            <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                            <span className="text-gray-700">{lesson}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to start learning?
            </h2>
            
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="text-4xl font-bold text-gray-900 mb-2">$69</div>
              <p className="text-gray-600 mb-6">One-time payment • Lifetime access</p>
              
              <a 
                href="https://learn.zerotocloud.co/p/5-aws-projects-to-become-a-cloud-security-engineer"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200 inline-block w-full text-center mb-4"
              >
                Enroll now for $69
              </a>
              
              <p className="text-sm text-gray-500">
                Join 500+ students already enrolled
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CloudSecurityEngineerProjectsPage;